import { useEffect, useMemo, useRef } from 'react';

import type { ReaderState } from '../types';
import { PageImage } from '../components/PageImage';
import { pageImageTransformFromRef } from '../pageTransforms';
import { cn } from '@/lib/utils';

export interface VerticalScrollProps {
  state: ReaderState;
  /** Fires whenever a new page enters the viewport. */
  onPageVisible?: (page: number) => void;
}

/**
 * Manga-style infinite vertical scroll. Each `<PageImage>` is rendered with
 * `eager` set ONLY when the page falls inside the preload window
 * (`prefs.preload` to either side of the active page); pages outside the
 * window mount in the idle `placeholder` state at `min-h-[40vh]`, so the
 * scroll layout is fully laid out up-front but only nearby pages actually
 * resolve their CDN URL.
 *
 * Page-detection uses IntersectionObserver, threshold 0.5: a page is
 * "current" when ≥50% of its bbox is visible.
 *
 * The eager-resolve window is the user-visible counterpart to B2's
 * eager-resolve flood: a 29-page e-hentai gallery used to fire 29
 * concurrent `showpage` calls on mount, which serialized through the
 * api.php rate limiter and produced H@H URLs that had often expired by
 * the time the user scrolled to them. Now we only resolve a few pages
 * around the current viewport position and re-resolve through
 * `state.invalidateUrl(page)` if an `<img>` load fails (wired via
 * `onRetry`).
 */
export function VerticalScroll({ state, onPageVisible }: VerticalScrollProps) {
  const { pages, prefs, resolveUrl, invalidateUrl, currentPage } = state;
  const containerRef = useRef<HTMLDivElement>(null);
  const pageRefs = useRef<Map<number, HTMLElement>>(new Map());
  /**
   * Latest page number the IO reported via `onPageVisible`. We compare
   * `currentPage` against this value in the scroll-into-view effect: when
   * they match, the change originated from the IO itself (the user scrolled
   * organically) and an imperative `scrollIntoView` would just fight the
   * scroll position. When they differ, the change came from outside (initial
   * mount, jump-to-page form, keyboard `Home`/`End`/jump) and we scroll.
   */
  const lastReportedRef = useRef<number | null>(null);
  // While a commanded smooth scroll is in flight, ignore observer reports for
  // pages passed along the way so the progress UI does not bounce through them.
  const pendingScrollTargetRef = useRef<number | null>(null);
  const pendingScrollTimerRef = useRef<number | null>(null);

  const onPageVisibleRef = useRef(onPageVisible);
  onPageVisibleRef.current = onPageVisible;

  useEffect(() => {
    return () => {
      if (pendingScrollTimerRef.current !== null) {
        window.clearTimeout(pendingScrollTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') return;
    const refs = pageRefs.current;
    const io = new IntersectionObserver(
      (entries) => {
        // Among entries that are intersecting, pick the one with the highest
        // intersectionRatio. That avoids flapping when two pages share the
        // viewport.
        let best: { page: number; ratio: number } | null = null;
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          const pageNum = Number((entry.target as HTMLElement).dataset.page);
          if (!Number.isFinite(pageNum)) continue;
          if (!best || entry.intersectionRatio > best.ratio) {
            best = { page: pageNum, ratio: entry.intersectionRatio };
          }
        }
        const pendingTarget = pendingScrollTargetRef.current;
        if (best && pendingTarget !== null && best.page !== pendingTarget) {
          return;
        }
        if (best && pendingTarget === best.page) {
          pendingScrollTargetRef.current = null;
          if (pendingScrollTimerRef.current !== null) {
            window.clearTimeout(pendingScrollTimerRef.current);
            pendingScrollTimerRef.current = null;
          }
        }
        if (best && lastReportedRef.current !== best.page) {
          lastReportedRef.current = best.page;
          onPageVisibleRef.current?.(best.page);
        }
      },
      { threshold: [0.25, 0.5, 0.75] },
    );

    for (const el of refs.values()) io.observe(el);
    return () => io.disconnect();
  }, []);

  /**
   * Imperative scroll-into-view, but ONLY for changes that did not originate
   * from the IntersectionObserver itself. Without this guard, async image
   * decoding expands `min-h-[40vh]` placeholders, the IO re-fires with a new
   * "highest ratio" page, the parent updates `currentPage`, this effect runs,
   * `scrollIntoView` is called, more layout shifts, IO re-fires, …
   *
   * The IO sets `lastReportedRef` immediately before notifying the parent;
   * if `currentPage === lastReportedRef.current` the change is IO-originated
   * and we leave the scroll position alone.
   *
   * Initial mount keeps `lastReportedRef.current === null`, so the deep-link
   * case (`/read/15` opening on page 15) still scrolls into view as expected.
   */
  useEffect(() => {
    if (lastReportedRef.current === currentPage) return;
    const el = pageRefs.current.get(currentPage);
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const inView = rect.top >= 0 && rect.top < window.innerHeight * 0.6;
    if (inView) {
      pendingScrollTargetRef.current = null;
      return;
    }
    pendingScrollTargetRef.current = currentPage;
    if (pendingScrollTimerRef.current !== null) {
      window.clearTimeout(pendingScrollTimerRef.current);
    }
    pendingScrollTimerRef.current = window.setTimeout(() => {
      if (pendingScrollTargetRef.current === currentPage) {
        pendingScrollTargetRef.current = null;
      }
      pendingScrollTimerRef.current = null;
    }, 1_500);
    el.scrollIntoView({ behavior: 'auto', block: 'start' });
  }, [currentPage]);

  const widthClass = useMemo(() => {
    switch (prefs.fit) {
      case 'width':
        return 'w-full';
      case 'height':
        return 'h-screen w-auto';
      case 'free':
      default:
        return 'w-full';
    }
  }, [prefs.fit]);

  /**
   * Eager-resolve window: how far around the active page we let `PageImage`
   * call `resolveUrl()`. We extend `prefs.preload` by +1 so the page
   * immediately after the preload tail starts resolving the moment it
   * enters the viewport (otherwise the user sees a brief loading flicker
   * crossing each window boundary).
   */
  const eagerRadius = Math.max(1, prefs.preload + 1);

  return (
    <div
      ref={containerRef}
      className="flex flex-col items-center gap-1 bg-background pb-12"
    >
      {pages.map((ref, idx) => {
        const pageNum = idx + 1;
        const isNear = Math.abs(pageNum - currentPage) <= eagerRadius;
        return (
          <div
            key={`${ref.comicId}-${pageNum}`}
            data-page={pageNum}
            ref={(el) => {
              if (el) pageRefs.current.set(pageNum, el);
              else pageRefs.current.delete(pageNum);
            }}
            className={cn('relative mx-auto flex w-full max-w-3xl justify-center', widthClass)}
          >
            <PageImage
              page={pageNum}
              eager={isNear}
              transform={pageImageTransformFromRef(ref)}
              resolveUrl={() => resolveUrl(pageNum).then((u) => u ?? undefined)}
              onRetry={() => invalidateUrl(pageNum)}
              className={cn('min-h-[40vh]', widthClass)}
            />
          </div>
        );
      })}
    </div>
  );
}
